import type {
    CadNextNotification,
    CadNextNotificationEntityEnumType,
    CadNextNotificationTypeEnumType,
} from '@mark43/cad-api';
import { chained } from './chained';
import { action, makeObservable, observable, toJS, untracked } from 'mobx';
import { notificationTypeToEntityType } from './event-notifications';

export type EventNotificationMap = Record<
    number, // the event id
    Partial<
        Record<
            CadNextNotificationEntityEnumType,
            Record<
                number, // the entity id
                Partial<Record<CadNextNotificationTypeEnumType, CadNextNotification>>
            >
        >
    >
>;

interface MultiProcessStore<T> {
    getState(): T;
    mergeState(externalState: EventNotificationMap): void;
}

export type OnEventNotificationRemoval = (args: {
    eventId: number;
    entityId: number;
    notifications: CadNextNotificationTypeEnumType[];
    options?: {
        localRemovalOnly?: boolean;
    };
}) => void;

type OnRequestEventNotificationsState = () => void;

type Options = {
    onEventNotificationRemoval: OnEventNotificationRemoval;
    onRequestEventNotificationsState: OnRequestEventNotificationsState;
};

function safelyCreateNotificationRecord(
    eventNotificationMap: EventNotificationMap,
    args: { eventId: number; entityId: number; notification: CadNextNotificationTypeEnumType }
) {
    const entityType = notificationTypeToEntityType[args.notification];
    if (!eventNotificationMap[args.eventId]) {
        eventNotificationMap[args.eventId] = {};
    }
    const entityTypeRecord = eventNotificationMap[args.eventId][entityType];
    if (!entityTypeRecord) {
        eventNotificationMap[args.eventId][entityType] = {};
    }
    const entityTypeRecordRecheck = eventNotificationMap[args.eventId][entityType];
    const entityIdRecord = entityTypeRecordRecheck?.[args.entityId];
    if (!entityIdRecord && entityTypeRecordRecheck) {
        entityTypeRecordRecheck[args.entityId] = {};
    }
}

function getRecordContainingNotification(
    eventNotificationMap: EventNotificationMap,
    args: { eventId: number; entityId: number; notification: CadNextNotificationTypeEnumType }
): undefined | Partial<Record<CadNextNotificationTypeEnumType, CadNextNotification>> {
    const notificationEntityType = notificationTypeToEntityType[args.notification];
    const notificationsForEvent = eventNotificationMap[args.eventId];
    if (!notificationsForEvent) {
        return;
    }
    const notificationsForEventEntityType = notificationsForEvent[notificationEntityType];
    if (!notificationsForEventEntityType) {
        return;
    }
    const notificationsForEventEntityTypeAndEntityId =
        notificationsForEventEntityType[args.entityId];
    return notificationsForEventEntityTypeAndEntityId;
}

function smartTrack<T, K>(accessor: (arg: T) => K): (arg: T) => K | undefined {
    return (arg: T) => {
        let val: K | undefined;
        untracked(() => {
            val = accessor(arg);
        });
        if (!val) {
            accessor(arg);
        }
        return val;
    };
}

export function getAssociatedEventsIds(
    notifications?: EventNotificationMap,
    eventId?: number
): number[] {
    if (!eventId || !notifications) return [];

    const currentEventNotifications = notifications[eventId];

    if (!currentEventNotifications) return [];

    const agencyEvents =
        currentEventNotifications[notificationTypeToEntityType['ASSOCIATED_EVENT']];
    if (!agencyEvents) return [];

    return Object.keys(agencyEvents)
        ?.filter((id) => Number(id) !== eventId)
        .map(Number);
}

export class EventNotificationsStore implements MultiProcessStore<EventNotificationMap> {
    @observable
    private eventNotifications: EventNotificationMap = {};
    private onEventNotificationRemoval: OnEventNotificationRemoval;
    private onRequestEventNotificationsState: OnRequestEventNotificationsState;
    constructor(options: Options) {
        this.onEventNotificationRemoval = options.onEventNotificationRemoval;
        this.onRequestEventNotificationsState = options.onRequestEventNotificationsState;
        makeObservable(this);
    }
    getState(): EventNotificationMap {
        return toJS(this.eventNotifications);
    }
    @action
    mergeState(externalState: EventNotificationMap): void {
        for (const eventId in externalState) {
            const eventRecord = externalState[eventId];
            if (eventRecord) {
                const notificationsForExternalEventRecord =
                    this.getAllNotificationsForEventRecord(eventRecord);
                for (const notification of notificationsForExternalEventRecord) {
                    this.setNotification(notification);
                }
            }
        }
    }
    requestEventNotificationsState(): void {
        this.onRequestEventNotificationsState();
    }
    @action
    setNotification(notification: CadNextNotification): void {
        safelyCreateNotificationRecord(this.eventNotifications, {
            eventId: notification.eventId,
            entityId: notification.entityId,
            notification: notification.notificationType,
        });
        const notificationsForEntityType =
            this.eventNotifications?.[notification.eventId]?.[notification.entityType];
        if (notificationsForEntityType) {
            notificationsForEntityType[notification.entityId][notification.notificationType] =
                notification;
        }
    }
    @action
    removeNotification(args: {
        eventId: number;
        entityId: number;
        notification: CadNextNotificationTypeEnumType;
        options?: {
            localRemovalOnly?: boolean;
        };
    }): void {
        if (!args.options?.localRemovalOnly) {
            this.onEventNotificationRemoval({
                ...args,
                notifications: [args.notification],
            });
        }
        const record = getRecordContainingNotification(this.eventNotifications, args);
        // This if statement is a hack to remove these notifications when dismissal is called from the legacy cad event panel. It can be removed
        // when we replace these with higher fidelity notifications - when removing this, we can remove the skipWholeEntityRemoval parameter above
        if (!record) {
            return;
        }
        delete record[args.notification];
    }
    @action
    removeNotifications(args: {
        eventId: number;
        entityId: number;
        notifications: CadNextNotificationTypeEnumType[];
        options?: {
            localRemovalOnly?: boolean;
        };
    }): void {
        if (!args.options?.localRemovalOnly) {
            this.onEventNotificationRemoval(args);
        }
        args.notifications.forEach((notification) => {
            if (notification === 'ASSOCIATED_EVENT') {
                const associatedEventsIds = getAssociatedEventsIds(
                    this.eventNotifications,
                    args.eventId
                );

                associatedEventsIds.forEach((entityId) => {
                    this.removeNotification({
                        eventId: args.eventId,
                        entityId,
                        notification,
                        options: args.options,
                    });
                });
            } else {
                this.removeNotification({
                    eventId: args.eventId,
                    entityId: args.entityId,
                    notification,
                    options: args.options,
                });
            }
        });
    }
    getNotification(args: {
        eventId: number;
        entityId: number;
        notification: CadNextNotificationTypeEnumType;
    }): CadNextNotification | undefined {
        return chained(
            smartTrack(
                (eventNotificationMap: EventNotificationMap) => eventNotificationMap[args.eventId]
            ),
            // @ts-expect-error
            smartTrack(
                (eventRecord: EventNotificationMap[number]) =>
                    eventRecord[notificationTypeToEntityType[args.notification]]
            ),
            smartTrack(
                (
                    entityTypeRecord: EventNotificationMap[number][CadNextNotificationEntityEnumType]
                ) => entityTypeRecord?.[args.entityId]
            ),
            (
                entityRecord: Exclude<
                    EventNotificationMap[number][CadNextNotificationEntityEnumType],
                    undefined
                >[number]
            ) => entityRecord[args.notification]
        )(this.eventNotifications);
    }
    getNotifications(args: {
        eventId: number;
        entityId: number;
        notifications: CadNextNotificationTypeEnumType[];
    }): CadNextNotification[] {
        return args.notifications
            .map((notification) =>
                this.getNotification({
                    eventId: args.eventId,
                    entityId: args.entityId,
                    notification,
                })
            )
            .filter((notification) => !!notification) as CadNextNotification[];
    }
    private getAllNotificationsForEventRecord(
        eventRecord: EventNotificationMap[number]
    ): CadNextNotification[] {
        let ret: CadNextNotification[] = [];
        for (const entityType in eventRecord) {
            const eventByEntityTypeRecord =
                eventRecord[entityType as CadNextNotificationEntityEnumType];
            for (const entityId in eventByEntityTypeRecord) {
                const eventNotificationsForEntityId =
                    eventByEntityTypeRecord[
                        entityId as unknown as keyof typeof eventByEntityTypeRecord
                    ];
                const notifications = Object.values(eventNotificationsForEntityId);
                ret = ret.concat(notifications);
            }
        }
        return ret;
    }
    getAllNotificationsForEvent(eventId: number): CadNextNotification[] {
        let eventRecord: (typeof this.eventNotifications)[number] = {};
        // consumers of this don't want to be subscribed to all changes to the event
        // notifications store, so we don't track the access to our top level map
        // and instead track just the access to the particular event's entry
        untracked(() => {
            eventRecord = this.eventNotifications[eventId];
        });
        if (!eventRecord) {
            // if the record for the event doesn't yet exist, we have to track it for now
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            this.eventNotifications[eventId];
            return [];
        } else {
            return this.getAllNotificationsForEventRecord(eventRecord);
        }
    }
    getAllNotificationsForEntityTypeForEvent(
        eventId: number,
        entityType: CadNextNotificationEntityEnumType
    ): CadNextNotification[] {
        return this.getAllNotificationsForEvent(eventId).filter(
            (notification) => notification.entityType === entityType
        );
    }
    @action
    removeAllNotificationsForEntityTypeForEvent(
        eventId: number,
        entityType: CadNextNotificationEntityEnumType,
        options?: {
            localRemovalOnly?: boolean;
        }
    ): void {
        this.getAllNotificationsForEntityTypeForEvent(eventId, entityType).forEach(
            (notification) => {
                this.removeNotification({
                    eventId,
                    notification: notification.notificationType,
                    entityId: notification.entityId,
                    options,
                });
            }
        );
    }
    @action
    removeAllNotificationsForEvent(eventId: number): void {
        const allNotificationsForEvent = this.getAllNotificationsForEvent(eventId);
        for (const notification of allNotificationsForEvent) {
            this.removeNotification({
                eventId: notification.eventId,
                entityId: notification.entityId,
                notification: notification.notificationType,
            });
        }
    }
}
